import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import api, { formatApiError } from "../lib/api";

const scoreColor = (score) =>
  score >= 90 ? "#10B981" : score >= 80 ? "#062A72" : score >= 70 ? "#0284C7" : score >= 60 ? "#D97706" : "#E11D48";

export default function CandidateReportModal({ candidate, jobId, open, onClose }) {
  const [downloading, setDownloading] = useState(false);
  if (!candidate) return null;
  const color = scoreColor(candidate.overall_score);
  const circumference = 2 * Math.PI * 34;
  const offset = circumference - (Math.min(candidate.overall_score, 100) / 100) * circumference;

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get(`/jobs/${jobId}/candidates/${candidate.id}/report.pdf`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${candidate.candidate_name.replace(/\s+/g, "_")}_hireqix_report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to generate PDF report.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="candidate-report-modal">
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="font-heading">{candidate.candidate_name}</DialogTitle>
          <button
            onClick={downloadPdf}
            disabled={downloading}
            data-testid="download-report-pdf-btn"
            className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150 disabled:opacity-60"
          >
            {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download PDF Report
          </button>
        </DialogHeader>

        {candidate.error ? (
          <div className="rounded-lg border-l-4 border-destructive bg-destructive/10 p-4 text-sm" data-testid="candidate-error-box">
            Error: {candidate.error}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-4 rounded-xl bg-muted p-4">
              <svg width="80" height="80" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="34" fill="none" stroke="currentColor" className="text-border" strokeWidth="8" />
                <circle
                  cx="40"
                  cy="40"
                  r="34"
                  fill="none"
                  stroke={color}
                  strokeWidth="8"
                  strokeDasharray={circumference}
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                  transform="rotate(-90 40 40)"
                  style={{ transition: "stroke-dashoffset 0.6s ease-out" }}
                />
                <text x="40" y="45" textAnchor="middle" fontSize="18" fontWeight="700" fill={color}>
                  {Math.round(candidate.overall_score)}
                </text>
              </svg>
              <div>
                <div className="text-lg font-semibold font-heading">{candidate.candidate_name}</div>
                <span
                  className="mt-1 inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium"
                  style={{ color, borderColor: color }}
                  data-testid="candidate-recommendation-badge"
                >
                  {candidate.recommendation}
                </span>
              </div>
            </div>

            <div>
              <h4 className="mb-2 font-semibold font-heading">Competency Evaluation</h4>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left">Competency</th>
                      <th className="px-3 py-2 text-left">Score</th>
                      <th className="px-3 py-2 text-left">Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidate.competencies.map((c, i) => (
                      <tr key={i} className="border-t border-border/60">
                        <td className="px-3 py-2 font-medium">{c.name}</td>
                        <td className="px-3 py-2 font-mono">{c.score}/5</td>
                        <td className="px-3 py-2 text-muted-foreground">{c.comments}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {candidate.strengths?.length > 0 && (
              <div>
                <h4 className="mb-2 font-semibold font-heading text-emerald-600 dark:text-emerald-400">Strengths</h4>
                <ul className="space-y-1 text-sm">
                  {candidate.strengths.map((s, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-emerald-500">•</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {candidate.major_gaps?.length > 0 && (
              <div>
                <h4 className="mb-2 font-semibold font-heading text-amber-600 dark:text-amber-400">Major Gaps</h4>
                <ul className="space-y-1 text-sm">
                  {candidate.major_gaps.map((s, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-amber-500">•</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {candidate.interview_questions?.length > 0 && (
              <div>
                <h4 className="mb-2 font-semibold font-heading">Suggested Interview Questions</h4>
                <ul className="space-y-1 text-sm">
                  {candidate.interview_questions.map((s, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-primary">•</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {candidate.hiring_verdict && (
              <div>
                <h4 className="mb-2 font-semibold font-heading">Hiring Verdict</h4>
                <div className="rounded-lg border-l-4 border-primary bg-muted p-4 text-sm leading-relaxed">{candidate.hiring_verdict}</div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
